export interface UserRoleFlags {
  isSuperadmin: boolean
  isSupport: boolean
  isCorporate: boolean
  isCore: boolean
  isTeam: boolean
  isPainter: boolean
  isAdmin: boolean
  isSales: boolean
  isCrew: boolean
  isViewer: boolean
  isFieldUser: boolean
  isOfficeUser: boolean
}

/**
 * Derive the boolean role flags for a user from their `app_metadata.roles`.
 *
 * This is the single source of truth for role-based UI gating. It is consumed by
 * `UserProvider` (which feeds `useUser()`), and can also be called directly with the
 * roles of an *arbitrary* user (e.g. the profile being edited in the Admin app, read
 * from Formik values) — contexts where there is no logged-in-user `UserProvider` for
 * that user.
 *
 * Notes on the legacy/new-style split:
 *  - `isAdmin`/`isSales`/`isCrew`/`isViewer` are legacy roles and are suppressed when the
 *    user also has a new-style role (`core`/`team`/`painter`), matching the original
 *    UserProvider logic.
 *  - `isFieldUser` (`crew || painter`) is the correct discriminator for "field user"
 *    gating, since modern painters use the `painter` role and are therefore NOT `isCrew`.
 */
export function getUserRoleFlags(roles: string[] = []): UserRoleFlags {
  const hasNewRole = roles.some((r) => r === 'core' || r === 'team' || r === 'painter')

  return {
    isSuperadmin: roles.indexOf('superadmin') !== -1,
    isSupport: roles.indexOf('support') !== -1,
    isCorporate: roles.indexOf('corporate') !== -1,
    isCore: roles.indexOf('core') !== -1,
    isTeam: roles.indexOf('team') !== -1,
    isPainter: roles.indexOf('painter') !== -1,
    isAdmin: roles.indexOf('admin') !== -1 && !hasNewRole,
    isSales: roles.indexOf('sales') !== -1 && !hasNewRole,
    isCrew: roles.indexOf('crew') !== -1 && !hasNewRole,
    isViewer: roles.indexOf('viewer') !== -1 && !hasNewRole,
    isFieldUser: roles.indexOf('crew') !== -1 || roles.indexOf('painter') !== -1,
    isOfficeUser: roles.indexOf('viewer') !== -1 || roles.indexOf('team') !== -1
  }
}
